import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  from,
  map,
  Observable,
  ObservableInput
} from 'rxjs';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthErrorCodes,
  ClientAuthErrorCodes,
  EventType,
  InteractionRequiredAuthErrorCodes,
  InteractionStatus,
  RedirectRequest
} from '@azure/msal-browser';
import { apiConfig, b2cPolicies } from '../auth-config';
import { BASE_PATH } from '../shared/data-upload.token';
import { resolveBasePath } from './base-path.utils';
import { environment } from '../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { navigateForHttpStatus } from '../interceptors/error.handler';
import { ElasticApmService } from './elastic-apm.service';
import { Role } from '../enums/roles.enum';
import { Router } from '@angular/router';
import { BrowserStorageService } from './browser-storage.service';

@Injectable()
export class AuthenticationService {
  private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly httpClient = inject(HttpClient);
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly elasticApmService = inject(ElasticApmService);
  private readonly localStorage = inject(BrowserStorageService);
  private readonly router = inject(Router);
  protected basePath = resolveBasePath(inject(BASE_PATH, { optional: true }), environment.dataUploadApiEndpoint, '/api/external');

  private ukhoRegex = /(?<=@)ukho\.gov\.uk/i;

  private readonly accountDetails: BehaviorSubject<AccountInfo | null> = new BehaviorSubject<AccountInfo | null>(null);
  private readonly objId: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly idToken: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly fullName: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>('');
  private readonly givenName: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>('');
  private readonly email: BehaviorSubject<string | unknown> = new BehaviorSubject<string | unknown>('');

  private readonly roles: BehaviorSubject<string[] | unknown> = new BehaviorSubject<string[] | unknown>([]);
  private readonly isUduUser: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private readonly isFtpUser: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private readonly userRoles: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
  private readonly isInternalUser: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  private readonly accessToken: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly isAuthed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly navigateAllowed = new BehaviorSubject<boolean>(true);

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.msalBroadcastService.msalSubject$.pipe(takeUntilDestroyed(destroyRef)).subscribe((event) => {
      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        this.setAccessToken((event.payload as AuthenticationResult)?.accessToken);
      }
    });

    this.checkIsAuthenticated();
  }

  public initializeBroadcast(): Observable<string> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None)
    );
  }

  public getUserRoles(): Observable<string[]> {
    return this.httpClient.request<string[]>('get', `${this.basePath}/user/get-roles`).pipe(
      map((roles: string[]) => {
        this.setUserRoles(roles);
        this.setIsUduUser(roles.includes(Role.User));
        this.setIsFtpUser(roles.includes(Role.Ftp));
        return roles;
      }),
      catchError((error: HttpErrorResponse): ObservableInput<never> => {
        this.checkIsAuthenticated();
        if (error.status === 403 || error.status === 401) {
          this.setUserRoles([]);
          this.setIsUduUser(false);
          return new Observable<never>((observer) => observer.error(new Error('The user is not assigned the role')));
        } else {
          this.setUserRoles([]);
          return new Observable<never>((observer) => observer.error(error));
        }
      })
    );
  }

  public login(userFlowRequest?: RedirectRequest) {
    const loginRedirectErrorHandler = {
      error: (e: { errorCode?: string; name?: string; message?: string }) => {
        // interaction_in_progress is a transient race condition that occurs when
        // handleRedirectObservable is still processing while login() is called.
        // The in-progress interaction will handle authentication, so this can be safely ignored.
        if (e.errorCode !== BrowserAuthErrorCodes.interactionInProgress) {
          this.elasticApmService.logError(e.name ?? 'login', `${e.errorCode}: ${e.message}`);
        }
      }
    };

    if (this.msalGuardConfig.authRequest) {
      this.msalService
        .loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest)
        .subscribe(loginRedirectErrorHandler);
    } else {
      this.msalService.loginRedirect(userFlowRequest).subscribe(loginRedirectErrorHandler);
    }
  }

  public getIsAuthedExternalUduUser(): Observable<boolean> {
    return combineLatest([
      this.isAuthed.asObservable(),
      this.isUduUser.asObservable(),
      this.isInternalUser.asObservable()
    ]).pipe(
      filter(([, isUduUser]) => isUduUser !== null),
      map(
        ([isAuthed, isUduUser, isInternalUser]: [boolean, boolean | null, boolean | null]) => isAuthed && !!isUduUser && !isInternalUser
      )
    );
  }

  public getIsAuthedInternalUser(): Observable<boolean> {
    return combineLatest([this.isAuthed.asObservable(), this.isInternalUser.asObservable()]).pipe(
      map(([isAuthed, isInternalUser]: [boolean, boolean | null]) => isAuthed && !!isInternalUser)
    );
  }

  public getIsAuthedInternalUserWhenReady(): Observable<boolean> {
    return combineLatest([
      this.msalBroadcastService.inProgress$,
      this.isAuthed.asObservable(),
      this.isInternalUser.asObservable(),
      this.accountDetails.asObservable()
    ]).pipe(
      filter(([inProgress, _isAuthed, isInternalUser, accountDetails]: [InteractionStatus, boolean, boolean | null, AccountInfo | null]) => 
        inProgress === InteractionStatus.None && 
        isInternalUser !== null && 
        accountDetails !== null
      ),
      map(([_inProgress, isAuthed, isInternalUser, _accountDetails]: [InteractionStatus, boolean, boolean | null, AccountInfo | null]) => 
        isAuthed && !!isInternalUser
      )
    );
  }

  public getIsValidUser(): Observable<boolean> {
    return combineLatest([this.getIsAuthedExternalUduUser(), this.getIsAuthedInternalUser()]).pipe(
      map(
        ([isAuthedExternalUduUser, isAuthedInternalUser]: [boolean, boolean]) =>
          isAuthedExternalUduUser || isAuthedInternalUser
      )
    );
  }

  public  checkIsAuthenticated(): void {
    return this.isAuthed.next(this.msalService.instance.getAllAccounts().length > 0);
  }

  public getIsUduUser(): Observable<boolean | null> {
    return this.isUduUser.asObservable();
  }

  public isAuthenticated$(): Observable<boolean> {
    return this.isAuthed.asObservable();
  }

  public isAuthenticated(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  public hasUduRole(): Observable<boolean | null> {
    return this.isUduUser.asObservable();
  }

  public setUserRoles(roles: string[]): void {
    return this.userRoles.next(roles);
  }

  public setIsUduUser(value: boolean): void {
    return this.isUduUser.next(value);
  }

  public setIsFtpUser(value: boolean): void {
    return this.isFtpUser.next(value);
  }

  public logout() {
    this.msalService.logoutRedirect().subscribe();
    this.resetAuthDetails();
  }

  public setNavigateAllowed(canNavigate: boolean) {
    this.navigateAllowed.next(canNavigate);
  }

  public getSignOutAllowedObservable(): Observable<boolean> {
    return this.navigateAllowed.asObservable();
  }

  public getNavigateAllowed(): boolean {
    return this.navigateAllowed.value;
  }

  public getAccountDetails(): Observable<AccountInfo | null> {
    return this.accountDetails.asObservable();
  }

  public setAccountDetails() {
    const accountDetails = this.msalService.instance.getAllAccounts()[0];
    if (accountDetails !== null && accountDetails !== undefined) {
      this.msalService.instance.setActiveAccount(accountDetails);
      const email = accountDetails.idTokenClaims?.email;

      if (email !== null && email !== undefined && typeof email === 'string') {
        this.isInternalUser.next(this.ukhoRegex.test(email));
      }

      this.roles.next(accountDetails.idTokenClaims?.role);
      this.accountDetails.next(accountDetails);
      this.fullName.next(accountDetails?.name?.replace(',', ''));
        const givenName = accountDetails.idTokenClaims?.given_name;
        this.givenName.next(typeof givenName === 'string' ? givenName : '');
    }
  }

  public editProfile() {
    const editProfileFlowRequest = {
      scopes: ['openid'],
      authority: b2cPolicies.authorities.userProfile.authority
    };

    this.login(editProfileFlowRequest);
  }

  public acquireTokenSilent(): Observable<unknown> {
    this.saveRedirectUrl();
    return from(
      this.msalService.instance
        .acquireTokenSilent({ account: this.msalService.instance.getAllAccounts()[0], scopes: apiConfig.b2cScopes })
        .then((authResult: AuthenticationResult) => {
          this.checkIsAuthenticated();
          this.setAccessToken(authResult.accessToken);

          const isExternalEmail = !this.ukhoRegex.test((authResult.idTokenClaims as { email?: string })?.email ?? '');
          if (isExternalEmail) {
            this.getUserRoles().subscribe({
              error: (error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                  this.elasticApmService.logError('getUserRoles', JSON.stringify(error));
                  navigateForHttpStatus(this.router, error.status);
                }
                // Plain Error (from 401/403 path in getUserRoles catchError):
                // isUduUser already set to false; handleLoginNavigation and
                // errorLoggingInterceptor handle navigation — no action needed here.
              }
            });
          } else {
            this.setIsUduUser(false);
          }

          this.elasticApmService.setUserContext(authResult.uniqueId, authResult.account.username);
          return authResult;
        })
        .catch((e) => {
          if (e.errorCode === InteractionRequiredAuthErrorCodes.interactionRequired) {
            this.elasticApmService.logError(e.name, `${e.errorCode}: ${e.errorMessage}`);
            this.login();
          } else if (e.errorCode !== 'no_account_error' && e.errorCode !== ClientAuthErrorCodes.endpointResolutionError) {
            this.elasticApmService.logError('acquireTokenSilent', JSON.stringify(e));
          }

          this.checkIsAuthenticated();
          return e;
        })
    ).pipe(
      map((authResponse) => {
        if (authResponse.account !== undefined && authResponse.account.idTokenClaims !== undefined) {
          const email = authResponse.account.idTokenClaims?.email;

          if (email !== null && email !== undefined && typeof email === 'string') {
            this.isInternalUser.next(this.ukhoRegex.test(email));
          }

          this.email.next(email);
          this.objId.next(authResponse.uniqueId);
          this.idToken.next(authResponse.idToken);

          this.roles.next(authResponse.account.idTokenClaims?.role);
        } else {
          console.warn('Auth account empty', authResponse);
        }
      }),
      catchError((error: HttpErrorResponse): ObservableInput<Error> => {
        if (error.status === 403 || error.status === 401) {
          this.setIsUduUser(false);
          return new Observable((observer) => observer.error(new Error('The user is not assigned the role')));
        } else {
          return new Observable((observer) => observer.error(error));
        }
      })
    );
  }

  public handleLoginNavigation(router: Router): Observable<void> {
    return combineLatest([this.isAuthenticated$(), this.getIsInternalUser(), this.hasUduRole()]).pipe(
    filter(([, isInternalUser, hasUduRole]) => isInternalUser !== null && hasUduRole !== null),
      map(([isAuthed, isInternalUser, hasUduRole]: [boolean, boolean | null, boolean | null]) => {
        if (isAuthed) {
          const redirectUrl = this.localStorage.getItem('loginRedirectUrl') as string | null;
          this.localStorage.setSessionStorageData('loginRedirectUrl', null);
          if (isInternalUser) {
            if (redirectUrl != null && redirectUrl.indexOf('/external') == -1) {
              void router.navigate([redirectUrl]);
            } else {
              void router.navigate(['/internal/dashboard']);
            }
          } 
          else {
            void (hasUduRole ? 
              (redirectUrl != null && redirectUrl.indexOf('/internal') == -1 ? router.navigate([redirectUrl]) : router.navigate(['/external/dashboard'])) : 
              router.navigate(['/permissions-error']));
          }
        } else {
          void router.navigate([]);
        }
      })
    );
  }

  public getFullName(): Observable<string | undefined> {
    return this.fullName.asObservable();
  }

  public getFullNameValue(): string | undefined {
    return this.fullName.value;
  }

  public getGivenName(): Observable<string | undefined> {
    return this.givenName.asObservable();
  }

  public getEmail(): Observable<string | unknown> {
    return this.email.asObservable();
  }

  public getEmailValue(): string | unknown {
    return this.email.value;
  }

  public getIsInternalUser(): Observable<boolean | null> {
    return this.isInternalUser.asObservable();
  }

  public getIsFtpUser(): Observable<boolean | null> {
    return this.isFtpUser.asObservable();
  }

  public getIsInternalUserValue(): boolean | null {
    return this.isInternalUser.value;
  }

  public getObjectId(): Observable<string | undefined> {
    return this.objId.asObservable();
  }

  public getIdToken(): Observable<string | undefined> {
    return this.idToken.asObservable();
  }

  public getAccessToken(): string {
    return this.accessToken.value;
  }

  public setAccessToken(accessToken: string): void {
    this.accessToken.next(accessToken);
  }

  public hasInternalDownloadPermission(): boolean {
    return this.hasInternalRole([Role.InternalUser, Role.InternalAdmin]);
  }

  public hasInternalFilesEditPermission(): boolean {
    return this.hasInternalRole([Role.InternalAdmin]);
  }
  
  public hasInternalDeletePermission(): boolean {
    return this.hasInternalRole([Role.InternalAdmin]);
  }

  public hasInternalEditPermission(): boolean {
    return this.hasInternalRole([Role.InternalAdmin]);
  }

  private hasInternalRole(requiredRoles: string[]): boolean {
    const roles = this.roles.value as string[] | unknown;

    if (roles === null || roles === undefined || !(roles instanceof Array))
      return false;

    return (!!this.getIsInternalUserValue() && requiredRoles.some(role => roles.includes(role)));
  }

  private resetAuthDetails() {
    this.fullName.next('');
    this.givenName.next('');
    this.email.next('');
    this.objId.next('');
    this.idToken.next('');
    this.accountDetails.next(null);
    this.roles.next([]);
    this.isUduUser.next(null);
    this.isFtpUser.next(null);
    this.userRoles.next([]);
    this.isInternalUser.next(null);
    this.accessToken.next('');
    this.isAuthed.next(false);
    this.navigateAllowed.next(true);
  }

  private saveRedirectUrl(): void {
    const url = window.location.pathname + window.location.search;

    if (url != '' &&
      url != '/' &&
      url.indexOf('redirect') == -1 &&
      url.indexOf('error') == -1
    )
    {
      this.localStorage.setSessionStorageData('loginRedirectUrl', url);
    }
  }
}
